import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { SettingsService } from '../services/settings.service';
import { MessageService } from 'primeng/api';
import { AzureService } from 'src/app/core/auth/azure.service';
import { Subject, takeUntil } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';

export interface AuditLog {
  auditId: string;
  businessId: string;
  userId: string;
  userName: string;
  action: string;
  ruleId?: string;
  changes: Record<string, unknown>;
  timestamp: number;
  details: string;
}

@Component({
  selector: 'app-audit-log',
  template: `
    <div class="audit-log-container">
      <div class="audit-log-header">
        <h3>Audit Log</h3>
        <p-button
          icon="pi pi-refresh"
          [rounded]="true"
          [text]="true"
          (click)="loadAuditLogs()"
          pTooltip="Refresh"
          tooltipPosition="left" />
      </div>
      <p-table
        [value]="auditLogs"
        [paginator]="true"
        [rows]="10"
        [rowsPerPageOptions]="[10, 25, 50]"
        [tableStyle]="{ 'min-width': '50rem' }"
        responsiveLayout="scroll"
        [globalFilterFields]="['userName', 'action']"
        [loading]="loading">
        <ng-template pTemplate="header">
          <tr>
            <th pSortableColumn="userName">User <p-sortIcon field="userName"></p-sortIcon></th>
            <th pSortableColumn="action">Action <p-sortIcon field="action"></p-sortIcon></th>
            <th pSortableColumn="ruleId">Rule ID <p-sortIcon field="ruleId"></p-sortIcon></th>
            <th>Changed Fields</th>
            <th pSortableColumn="timestamp">Timestamp <p-sortIcon field="timestamp"></p-sortIcon></th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-log>
          <tr>
            <td>
              <div class="user-cell">
                <span class="user-name">{{ log.userName }}</span>
                <span class="user-id">{{ log.userId }}</span>
              </div>
            </td>
            <td>
              <span [ngClass]="'action-badge action-' + log.action.toLowerCase()">
                {{ log.action }}
              </span>
            </td>
            <td>
              <span class="rule-id">{{ log.ruleId ? (log.ruleId | slice:0:8) + '...' : 'N/A' }}</span>
            </td>
            <td>
              <button (click)="openChangesDialog(log)" class="changes-button">
                {{ getChangeSummary(log.changes) }}
              </button>
            </td>
            <td>{{ convertTimestamp(log.timestamp) | date: 'medium' }}</td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="5" class="empty-message">
              <i class="pi pi-inbox"></i>
              <span>No audit logs found for this business area</span>
            </td>
          </tr>
        </ng-template>
      </p-table>

      <p-dialog
        [(visible)]="displayChangesDialog"
        [header]="'Changes Details'"
        [modal]="true"
        [style]="{ width: '50vw' }"
        [breakpoints]="{ '960px': '75vw', '640px': '90vw' }">
        <div class="changes-dialog-content">
          <div *ngIf="selectedChanges && Object.keys(selectedChanges).length > 0; else noChanges">
            <div *ngFor="let field of Object.keys(selectedChanges)" class="change-item">
              <div class="field-name">{{ field }}</div>
              <div class="change-row">
                <div class="change-section">
                  <div class="label">Before:</div>
                  <div class="value">{{ formatValue(getChangeValue(field, 'before')) }}</div>
                </div>
                <div class="arrow">→</div>
                <div class="change-section">
                  <div class="label">After:</div>
                  <div class="value">{{ formatValue(getChangeValue(field, 'after')) }}</div>
                </div>
              </div>
            </div>
          </div>
          <ng-template #noChanges>
            <div class="no-changes">No changes were made</div>
          </ng-template>
        </div>
      </p-dialog>
    </div>
  `,
  styles: [
    `
      .audit-log-container {
        padding: 1.5rem;
        background: white;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        margin: 1rem;
      }

      .audit-log-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1.5rem;

        h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #333;
          font-family: "Siemens Sans", sans-serif;
        }
      }

      .user-cell {
        display: flex;
        flex-direction: column;
        gap: 2px;

        .user-name {
          font-weight: 500;
          color: #333;
        }

        .user-id {
          font-size: 10px;
          color: #999;
        }
      }

      .action-badge {
        padding: 4px 10px;
        border-radius: 12px;
        font-size: 11px;
        font-weight: 600;
        display: inline-block;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .action-create {
        background: #e8f5e9;
        color: #2e7d32;
      }

      .action-update {
        background: #e3f2fd;
        color: #1565c0;
      }

      .action-delete {
        background: #ffebee;
        color: #c62828;
      }

      .rule-id {
        font-family: monospace;
        font-size: 11px;
        background: #f5f5f5;
        padding: 2px 6px;
        border-radius: 4px;
        color: #666;
      }

      .changes-list {
        background: #f8f9fa;
        padding: 6px 8px;
        border-radius: 4px;
        font-size: 12px;
        color: #333;
        font-weight: 500;
        display: inline-block;
        cursor: help;
        border-left: 3px solid #8a00e5;
      }

      .changes-button {
        background: #f8f9fa;
        border: none;
        padding: 6px 12px;
        border-radius: 4px;
        font-size: 12px;
        color: #8a00e5;
        font-weight: 500;
        cursor: pointer;
        border-left: 3px solid #8a00e5;
        transition: all 0.2s ease;
      }

      .changes-button:hover {
        background: #f3e8ff;
        box-shadow: 0 2px 6px rgba(138, 0, 229, 0.2);
      }

      .changes-dialog-content {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
      }

      .change-item {
        border: 1px solid #e8e8e8;
        border-radius: 6px;
        padding: 1rem;
        background: #fafafa;
      }

      .field-name {
        font-weight: 600;
        color: #333;
        margin-bottom: 0.75rem;
        padding-bottom: 0.75rem;
        border-bottom: 2px solid #8a00e5;
        font-family: monospace;
        font-size: 13px;
      }

      .change-row {
        display: flex;
        gap: 1rem;
        align-items: center;
      }

      .change-section {
        flex: 1;
      }

      .change-section .label {
        font-size: 11px;
        font-weight: 600;
        color: #666;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 0.5rem;
      }

      .change-section .value {
        background: white;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        padding: 0.75rem;
        font-family: monospace;
        font-size: 12px;
        color: #333;
        word-break: break-word;
        min-height: 40px;
        display: flex;
        align-items: center;
      }

      .arrow {
        font-size: 18px;
        color: #8a00e5;
        font-weight: 600;
      }

      .no-changes {
        text-align: center;
        padding: 2rem;
        color: #999;
        font-size: 14px;
      }

      .empty-message {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 2rem;
        color: #999;

        i {
          font-size: 2rem;
        }
      }

      :deep(.p-datatable) {
        font-size: 13px;
        font-family: "Siemens Sans", sans-serif;
      }

      :deep(.p-datatable .p-datatable-thead > tr > th) {
        background: #f8f9fa;
        color: #333;
        font-weight: 600;
        padding: 14px 12px;
        border-color: #e8e8e8;
      }

      :deep(.p-datatable .p-datatable-tbody > tr > td) {
        padding: 12px;
        border-color: #f0f0f0;
        vertical-align: middle;
      }

      :deep(.p-datatable .p-datatable-tbody > tr:hover) {
        background: #f8f9fa;
      }

      :deep(.p-paginator) {
        padding: 1rem;
        border-top: 1px solid #e8e8e8;
      }
    `,
  ],
  standalone: true,
  imports: [CommonModule, TableModule, ButtonModule, TooltipModule, DialogModule],
})
export class AuditLogComponent implements OnInit, OnDestroy {
  auditLogs: AuditLog[] = [];
  loading = false;
  selectedBusinessArea = '';
  displayChangesDialog = false;
  selectedChanges: Record<string, unknown> = {};

  private readonly destroy$ = new Subject<void>();
  private settingsService = inject(SettingsService);
  private messageService = inject(MessageService);
  private azureService = inject(AzureService);

  ngOnInit(): void {
    this.azureService.selectedBusinessArea
      .pipe(takeUntil(this.destroy$))
      .subscribe((businessArea) => {
        this.selectedBusinessArea = businessArea;
        this.loadAuditLogs();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAuditLogs(): void {
    this.loading = true;
    this.settingsService.getAuditLog(this.selectedBusinessArea).subscribe({
      next: (auditLogs: AuditLog[]) => {
        this.auditLogs = auditLogs || [];
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading audit logs:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load audit logs',
        });
        this.loading = false;
      },
    });
  }

  convertTimestamp(timestamp: number): Date {
    return new Date(timestamp);
  }

  getChangeSummary(changes: Record<string, unknown>): string {
    if (!changes || Object.keys(changes).length === 0) {
      return 'No changes';
    }
    const fields = Object.keys(changes);
    if (fields.length === 1) {
      return `${fields.length} field changed`;
    }
    return `${fields.length} fields changed`;
  }

  formatChangesDetailed(changes: Record<string, unknown>): string {
    if (!changes || Object.keys(changes).length === 0) {
      return 'No changes were made';
    }

    const details: string[] = [];
    for (const [field, value] of Object.entries(changes)) {
      const change = value as Record<string, unknown>;
      const before = change['before'] === null ? '(empty)' : JSON.stringify(change['before']);
      const after = change['after'] === null ? '(empty)' : JSON.stringify(change['after']);
      details.push(`${field}:\n  Before: ${before}\n  After: ${after}`);
    }

    return details.join('\n\n');
  }

  openChangesDialog(log: AuditLog): void {
    this.selectedChanges = log.changes as Record<string, unknown>;
    this.displayChangesDialog = true;
  }

  formatValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '(empty)';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }
    if (typeof value === 'number') {
      return value.toString();
    }
    return JSON.stringify(value);
  }

  getChangeValue(field: string, key: 'before' | 'after'): unknown {
    const change = this.selectedChanges[field] as Record<string, unknown>;
    return change ? change[key] : undefined;
  }

  Object = Object;
}
